<template>
  <div class="d-flex flex-column flex-lg-row">
    <div v-background-3 class="p-3 w-100 rounded mb-3">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <h5 class="m-0">Gestion des libellés de compétence</h5>
        <button class="btn btn-primary" @click="openModal">
          Ajouter un libellé de compétence
        </button>
      </div>

      <!-- Champ de recherche et options de tri -->
      <div class="d-flex flex-wrap justify-content-between mb-3">
        <div class="input-group mb-2 mr-sm-2" style="max-width: 300px;">
          <div class="input-group-prepend">
            <div class="input-group-text">
              <BaseIcon
                name="search"
                color="#333333"
                width="20"
                height="20"
              ></BaseIcon>
            </div>
          </div>
          <input
            type="text"
            class="form-control"
            placeholder="Rechercher..."
            v-model="searchQuery"
            @input="applySearch"
          />
        </div>

        <div class="d-flex align-items-center">
          <label class="mr-2 mb-0">Trier par :</label>
          <select
            class="form-control form-control-sm mr-2"
            v-model="sortField"
            @change="applySort"
          >
            <option value="description">Description</option>
          </select>

          <button
            class="btn btn-sm"
            :class="sortOrder === 'asc' ? 'btn-primary' : 'btn-outline-primary'"
            @click="toggleSortOrder"
          >
            <i
              :class="
                sortOrder === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
              "
            ></i>
            {{ sortOrder === "asc" ? "ASC" : "DESC" }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="d-flex justify-content-center">
        <Loader />
      </div>
      <div v-else>
        <table class="table table-striped">
          <thead>
            <tr>
              <th @click="setSortField('description')" class="sortable-header">
                Description
                <i
                  v-if="sortField === 'description'"
                  :class="
                    sortOrder === 'asc'
                      ? 'fas fa-sort-up ml-1'
                      : 'fas fa-sort-down ml-1'
                  "
                ></i>
              </th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="libelle in filteredAndSortedLibelles" :key="libelle.id">
              <td>{{ libelle.description }}</td>
              <td class="text-right">
                <div class="btn-group">
                  <button
                    class="btn btn-sm btn-warning"
                    @click="editLibelle(libelle)"
                  >
                    Modifier
                  </button>
                  <button
                    class="btn btn-sm btn-danger ml-2"
                    @click="deleteLibelle(libelle.id)"
                  >
                    Supprimer
                  </button>
                </div>
              </td>
            </tr>
            <!-- Message quand aucun résultat trouvé -->
            <tr v-if="filteredAndSortedLibelles.length === 0">
              <td colspan="2" class="text-center">
                <em
                  >Aucun libellé de compétence ne correspond à votre
                  recherche</em
                >
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Compteur d'éléments -->
        <div class="text-muted small">
          {{ filteredAndSortedLibelles.length }} libellé(s) sur
          {{ libelles.length }}
        </div>
      </div>
    </div>

    <!-- Modal pour ajouter/modifier un libellé -->
    <modal
      ref="libelleModal"
      title="Libellé de Compétence"
      subtitle="Ajouter ou modifier un libellé de compétence"
      id="modal_competence_libelle"
      @save="saveLibelle"
      @close="closeModal"
      btn=""
      :btns="false"
    >
      <form @submit.prevent="saveLibelle">
        <div class="form-group">
          <label for="description">Description</label>
          <input
            type="text"
            id="description"
            v-model="form.description"
            class="form-control"
            required
          />
        </div>
        <button type="submit" class="btn btn-primary">Enregistrer</button>
      </form>
    </modal>
  </div>
</template>

<script>
import Loader from "@/components/bases/Loader.vue";
import Modal from "@/components/bases/Modal.vue";
import BaseIcon from "@/components/bases/Icon.vue";

export default {
  components: {
    Loader,
    Modal,
    BaseIcon
  },
  data() {
    return {
      libelles: [], // Liste des libellés de compétence
      loading: false,
      form: {
        id: null,
        description: ""
      },
      // Propriétés pour la recherche et le tri
      searchQuery: "",
      sortField: "description",
      sortOrder: "asc",
      filteredLibelles: [] // Libellés filtrés
    };
  },
  computed: {
    filteredAndSortedLibelles() {
      return this.filteredLibelles;
    }
  },
  methods: {
    closeModal() {
      this.resetForm();
      this.$refs.libelleModal.hide();
    },

    // Récupérer les libellés de compétence
    fetchLibelles() {
      this.loading = true;
      this.$http
        .get("/admin/competence_libelle")
        .then(response => {
          this.libelles = response.data;
          this.applySearch();
        })
        .catch(error => console.error(error))
        .finally(() => {
          this.loading = false;
        });
    },

    // Filtrer les libellés selon la recherche
    applySearch() {
      const query = this.searchQuery.toLowerCase().trim();

      if (!query) {
        this.filteredLibelles = [...this.libelles];
      } else {
        this.filteredLibelles = this.libelles.filter(libelle => {
          return (
            libelle.description &&
            libelle.description.toLowerCase().includes(query)
          );
        });
      }

      this.applySort();
    },

    // Trier les libellés
    applySort() {
      this.filteredLibelles.sort((a, b) => {
        const valueA = a[this.sortField] || "";
        const valueB = b[this.sortField] || "";

        if (typeof valueA === "string") {
          return this.sortOrder === "asc"
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        } else {
          return this.sortOrder === "asc" ? valueA - valueB : valueB - valueA;
        }
      });
    },

    // Définir le champ de tri
    setSortField(field) {
      if (this.sortField === field) {
        this.toggleSortOrder();
      } else {
        this.sortField = field;
        this.sortOrder = "asc";
      }
      this.applySort();
    },

    // Inverser l'ordre de tri
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      this.applySort();
    },

    // Ouvrir le modal pour ajouter un libellé
    openModal() {
      this.resetForm();
      this.$refs.libelleModal.show();
    },

    // Remplir le formulaire pour modifier un libellé
    editLibelle(libelle) {
      this.form = { ...libelle };
      this.$refs.libelleModal.show();
    },

    // Réinitialiser le formulaire
    resetForm() {
      this.form = {
        id: null,
        description: ""
      };
    },

    // Enregistrer un libellé (ajout ou modification)
    saveLibelle() {
      const url = this.form.id
        ? `/admin/competence_libelle/${this.form.id}`
        : `/admin/competence_libelle`;
      const method = this.form.id ? "put" : "post";

      this.$http[method](url, this.form)
        .then(() => {
          this.$refs.libelleModal.hide();
          this.fetchLibelles();
          // Message de confirmation
          this.$toast.success(
            this.form.id
              ? "Libellé de compétence modifié avec succès"
              : "Libellé de compétence ajouté avec succès"
          );
        })
        .catch(error => {
          console.error(error);
          // Message d'erreur
          this.$toast.error("Une erreur est survenue lors de l'enregistrement");
        });
    },

    // Supprimer un libellé
    deleteLibelle(id) {
      if (
        confirm(
          "Êtes-vous sûr de vouloir supprimer ce libellé de compétence ?\n\n ATTENTION : Assurez vous qu'il n'est pas utilisé par des compétences existantes avant de le supprimer."
        )
      ) {
        this.$http
          .delete(`/admin/competence_libelle/${id}`)
          .then(() => {
            this.fetchLibelles();
            this.$toast.success("Libellé de compétence supprimé avec succès");
          })
          .catch(error => {
            console.error(error);
            this.$toast.error(
              "Impossible de supprimer ce libellé car il est utilisé par des compétences"
            );
          });
      }
    }
  },
  mounted() {
    this.fetchLibelles();
  }
};
</script>

<style scoped>
/* Styles pour les en-têtes de colonnes triables */
.sortable-header {
  cursor: pointer;
  user-select: none;
  position: relative;
}

.sortable-header:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Animation de transition pour le tri */
.table tbody tr {
  transition: all 0.3s ease;
}

/* Style pour l'icône de recherche */
.input-group-text {
  background-color: #f8f9fa;
  border-right: none;
  height: 38px;
}

input.form-control:focus + .input-group-prepend .input-group-text {
  border-color: #80bdff;
}
</style>
